/**
 * Create a Gear Swap listing: header (name, location, SALE/TRADE/ISO, price if SALE)
 * and editable grid with text + media only.
 */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, addDoc, getDocs, query, where, doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import * as DocumentPicker from 'expo-document-picker';
import { useUser } from '../components/LoginPersistence';
import EditablePostGrid from '../components/EditablePostGrid';
import { MoveModeDesktopNote } from '../components/MoveModeDesktopNote';
import { GRID_COLS, GRID_ROWS } from '../utils/postGridEngine';
import { getResponsiveValue } from '../styles/breakpoints';

const ACCENT = '#424251';
const CELL_SIZE = getResponsiveValue({ mobile: 90, tablet: 110, laptop: 120, desktop: 120 });

const LISTING_TYPES = [
  { id: 'SALE', label: 'SALE', color: '#0a59f0' },
  { id: 'TRADE', label: 'TRADE', color: '#03bb8d' },
  { id: 'ISO', label: 'ISO', color: '#4e5067' },
];

const GEAR_SWAP_WIDGET_TYPES = [
  { id: 'text', label: 'Text', icon: 'document-text-outline' },
  { id: 'file', label: 'Media', icon: 'image-outline' },
  { id: 'audio', label: 'Audio', icon: 'musical-notes-outline' },
  { id: 'map', label: 'Map', icon: 'map-outline' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar-outline' },
];

const CARD_BG_SWATCHES = [
  // Neutrals
  '#ffffff', '#f5f0e8', '#e8e8e8', '#1a1a1a', '#0f172a',
  // Pastels
  '#feffe4', '#fef9c3', '#dcfce7', '#dbeafe', '#ede9fe',
  '#fce7f3', '#ffedd5', '#f0fdf4', '#ecfeff', '#fff1f2',
  // Vibrant
  '#1e3a5f', '#1a472a', '#4a1942', '#7c2d12', '#1c1917',
  // Brand-ish
  '#1075ae', '#2d6a4f', '#6d28d9', '#dc2626', '#d97706',
];

const parseCalendarWidgetValue = (raw) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {
    return { date: raw };
  }
  return null;
};

const enumerateDateKeysInclusive = (startKey, endKey) => {
  const out = [];
  const start = new Date(`${startKey}T00:00:00`);
  const end = new Date(`${endKey}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return out;
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = `${cursor.getMonth() + 1}`.padStart(2, '0');
    const d = `${cursor.getDate()}`.padStart(2, '0');
    out.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
};

const extractCalendarRangesFromItems = (items) => {
  const ranges = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (item?.type !== 'calendar') return;
    const parsed = parseCalendarWidgetValue(item.value);
    const startDate = parsed?.startDate || parsed?.date;
    const endDate = parsed?.endDate || startDate;
    if (!startDate) return;
    ranges.push({
      widgetId: item.id,
      startDate,
      endDate,
      title: parsed?.title || '',
    });
  });
  return ranges;
};

const sanitizeForFirestore = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeForFirestore(entry))
      .filter((entry) => entry !== undefined);
  }
  if (typeof value === 'object') {
    const sanitized = {};
    Object.entries(value).forEach(([key, nested]) => {
      const cleaned = sanitizeForFirestore(nested);
      if (cleaned !== undefined) sanitized[key] = cleaned;
    });
    return sanitized;
  }
  return value;
};

const TEMPLATES = [
  {
    id: 'blank', label: 'Blank', icon: 'grid-outline',
    build: () => Array.from({ length: 15 }, (_, i) => ({
      id: `cell-${i}`, type: 'text', value: '', x: i % 3, y: Math.floor(i / 3), w: 1, h: 1,
    })),
  },
  {
    id: 'header', label: 'Header', icon: 'albums-outline',
    build: () => [
      { id: 'cell-0', type: 'text', value: '', x: 0, y: 0, w: 3, h: 1 },
      ...Array.from({ length: 12 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '', x: i % 3, y: Math.floor(i / 3) + 1, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'portrait', label: 'Portrait', icon: 'phone-portrait-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 1, h: 3 },
      ...Array.from({ length: 6 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '', x: (i % 2) + 1, y: Math.floor(i / 2), w: 1, h: 1,
      })),
      ...Array.from({ length: 6 }, (_, i) => ({
        id: `cell-${i + 7}`, type: 'text', value: '', x: i % 3, y: Math.floor(i / 3) + 3, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'feature', label: 'Feature', icon: 'expand-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 2, h: 2 },
      { id: 'cell-1', type: 'text', value: '', x: 2, y: 0, w: 1, h: 1 },
      { id: 'cell-2', type: 'text', value: '', x: 2, y: 1, w: 1, h: 1 },
      ...Array.from({ length: 9 }, (_, i) => ({
        id: `cell-${i + 3}`, type: 'text', value: '', x: i % 3, y: Math.floor(i / 3) + 2, w: 1, h: 1,
      })),
    ],
  },
  {
    id: 'banner', label: 'Banner', icon: 'image-outline',
    build: () => [
      { id: 'cell-0', type: 'file', value: '', x: 0, y: 0, w: 3, h: 2 },
      ...Array.from({ length: 9 }, (_, i) => ({
        id: `cell-${i + 1}`, type: 'text', value: '', x: i % 3, y: Math.floor(i / 3) + 2, w: 1, h: 1,
      })),
    ],
  },
];

function isGridCellFilled(item) {
  if (!item) return false;
  const v = item.value;
  if (v != null && String(v).trim() !== '') return true;
  if (item.type === 'file' && v) {
    try {
      const parsed = JSON.parse(v);
      if (parsed?.url) return true;
    } catch (_) {}
  }
  return false;
}

function mergeTemplateWithCurrent(currentItems, newTemplateItems) {
  const current = (currentItems || []).slice();
  const newSlots = (newTemplateItems || []).slice().sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
  const currentSorted = current.sort((a, b) => {
    const fillA = isGridCellFilled(a) ? 1 : 0;
    const fillB = isGridCellFilled(b) ? 1 : 0;
    if (fillB !== fillA) return fillB - fillA;
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
  return newSlots.map((slot, i) => {
    if (i < currentSorted.length) {
      const cur = currentSorted[i];
      return {
        ...slot,
        type: cur.type,
        value: cur.value,
        cellBg: cur.cellBg,
        cellBorderRadius: cur.cellBorderRadius,
        cellBorderRadiusPercent: cur.cellBorderRadiusPercent,
        fontSize: cur.fontSize,
        textAlign: cur.textAlign,
        textColor: cur.textColor,
        fontFamily: cur.fontFamily,
        linkDisplayMode: cur.linkDisplayMode,
        linkButtonLabel: cur.linkButtonLabel,
        linkButtonBg: cur.linkButtonBg,
        linkButtonFg: cur.linkButtonFg,
        linkButtonShape: cur.linkButtonShape,
        linkButtonSize: cur.linkButtonSize,
      };
    }
    return { ...slot };
  });
}

export default function GearSwapPostCreate() {
  const navigation = useNavigation();
  const route = useRoute();
  const { user: userData } = useUser();
  const { editListingId = null, existingListing = null } = route.params || {};
  const isEditing = !!editListingId;

  const initialItemsEmpty = useMemo(() =>
    Array.from({ length: GRID_COLS * GRID_ROWS }, (_, i) => ({
      id: `cell-${i}`,
      type: 'text',
      value: '',
      x: i % GRID_COLS,
      y: Math.floor(i / GRID_COLS),
      w: 1,
      h: 1,
    })),
  []);

  const [posting, setPosting] = useState(false);
  const [location, setLocation] = useState(existingListing?.location ?? '');
  const [listingType, setListingType] = useState(existingListing?.listingType ?? 'SALE');
  const [price, setPrice] = useState(existingListing?.listingType === 'SALE' ? (existingListing?.price ?? '') : '');
  const [uploadingCellId, setUploadingCellId] = useState(null);

  const [items, setItems] = useState(
    existingListing?.items && Array.isArray(existingListing.items) ? existingListing.items : initialItemsEmpty
  );
  const [cardBackground, setCardBackground] = useState(existingListing?.cardBackground ?? null);
  const [headerTextColor, setHeaderTextColor] = useState(existingListing?.headerTextColor ?? null);
  const [cardBgPanelOpen, setCardBgPanelOpen] = useState(false);
  const [cardBgTab, setCardBgTab] = useState('color');
  const [cardBgColorInput, setCardBgColorInput] = useState('');
  const [uploadingCardBg, setUploadingCardBg] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [gridMode, setGridMode] = useState('move');
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyVersion, setHistoryVersion] = useState(0);

  const displayName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ') ||
    userData?.username || userData?.email?.split('@')[0] || 'User';

  const handleFileUpload = useCallback(async (id, cellType = 'file') => {
    try {
      setUploadingCellId(id);
      const acceptTypes = cellType === 'audio' ? ['audio/*'] : ['image/*', 'video/*', 'audio/*', 'application/pdf'];
      const result = await DocumentPicker.getDocumentAsync({ type: acceptTypes, copyToCacheDirectory: true });
      if (!result.canceled && result.assets?.length > 0) {
        const file = result.assets[0];
        const response = await fetch(file.uri);
        const fileBlob = await response.blob();
        const storage = getStorage();
        const uid = userData?.uid || 'anonymous';
        const storageRef = ref(storage, `gearSwapPosts/${uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        const downloadURL = await getDownloadURL(storageRef);
        let mimeType = file.mimeType;
        if (!mimeType) {
          if (/\.(mp4|webm|mov|avi|m4v)$/i.test(file.name || '')) mimeType = 'video/mp4';
          else if (/\.(mp3|wav|ogg|aac|flac|m4a)$/i.test(file.name || '')) mimeType = 'audio/mpeg';
          else if (/\.pdf$/i.test(file.name || '')) mimeType = 'application/pdf';
          else mimeType = 'image/*';
        }
        const value = JSON.stringify({ url: downloadURL, name: file.name, mimeType });
        setItemsWithHistory(prev => prev.map(i => i.id === id ? { ...i, value } : i));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      Alert.alert('Upload failed', 'Failed to upload file. Please try again.');
    } finally {
      setUploadingCellId(null);
    }
  }, [userData?.uid]);

  const setItemsWithHistory = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      undoStackRef.current.push(prev);
      if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      redoStackRef.current = [];
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  const handleUndo = useCallback(() => {
    setItems((current) => {
      const previous = undoStackRef.current.pop();
      if (!previous) return current;
      redoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return previous;
    });
  }, []);

  const handleRedo = useCallback(() => {
    setItems((current) => {
      const next = redoStackRef.current.pop();
      if (!next) return current;
      undoStackRef.current.push(current);
      setHistoryVersion((v) => v + 1);
      return next;
    });
  }, []);

  const handleCardBgUpload = useCallback(async (bgType) => {
    try {
      setUploadingCardBg(true);
      const result = await DocumentPicker.getDocumentAsync({
        type: bgType === 'video' ? ['video/*'] : ['image/*'],
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets?.length > 0) {
        const file = result.assets[0];
        const response = await fetch(file.uri);
        const fileBlob = await response.blob();
        const storage = getStorage();
        const uid = userData?.uid || 'anonymous';
        const storageRef = ref(storage, `gearSwapPostBg/${uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        const url = await getDownloadURL(storageRef);
        setCardBackground({ type: bgType, value: url });
      }
    } catch (e) {
      Alert.alert('Upload failed', 'Could not upload background.');
    } finally {
      setUploadingCardBg(false);
    }
  }, [userData?.uid]);

  const syncCalendarEventsForListing = useCallback(async ({ db, listingId, listingTitle, listingLocation, listingItems }) => {
    if (!db || !listingId || !userData?.uid) return;

    const existingQ = query(
      collection(db, 'users', userData.uid, 'calendarEvents'),
      where('gearSwapPostId', '==', listingId)
    );
    const existingSnap = await getDocs(existingQ);
    await Promise.all(
      existingSnap.docs
        .filter((d) => d.data()?.autoCreatedFromPost === true)
        .map((d) => deleteDoc(d.ref))
    );

    const ranges = extractCalendarRangesFromItems(listingItems);
    if (!ranges.length) return;

    const targetCollection = collection(db, 'users', userData.uid, 'calendarEvents');
    for (let i = 0; i < ranges.length; i += 1) {
      const range = ranges[i];
      const dayKeys = enumerateDateKeysInclusive(range.startDate, range.endDate);
      for (const dateKey of dayKeys) {
        await addDoc(targetCollection, {
          title: range.title || listingTitle || 'Gear swap listing',
          date: dateKey,
          notes: listingLocation || '',
          color: '#f59e0b',
          gearSwapPostId: listingId,
          eventType: 'custom',
          sortOrder: i,
          autoCreatedFromPost: true,
          autoSourceWidgetId: range.widgetId || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  }, [userData?.uid]);

  const handlePost = useCallback(async () => {
    if (!userData?.uid) {
      Alert.alert('Sign in required', 'Please sign in to post a listing.');
      return;
    }
    if (listingType === 'SALE' && !price.trim()) {
      Alert.alert('Price required', 'Please enter a price for SALE listings.');
      return;
    }
    try {
      setPosting(true);
      const db = getFirestore();
      const sanitizedItems = sanitizeForFirestore(items);
      if (isEditing && editListingId) {
        const updatePayload = {
          location: location || '',
          listingType,
          items: sanitizedItems,
          cardBackground: cardBackground || null,
          headerTextColor: headerTextColor || null,
          updatedAt: serverTimestamp(),
        };
        if (listingType === 'SALE') updatePayload.price = price.trim(); else updatePayload.price = null;
        await updateDoc(doc(db, 'gearSwapPosts', editListingId), updatePayload);
        await syncCalendarEventsForListing({
          db,
          listingId: editListingId,
          listingTitle: displayName || '',
          listingLocation: location || '',
          listingItems: sanitizedItems,
        });
      } else {
        // Enforce max 3 Gear Swap listings per user
        const q = query(
          collection(db, 'gearSwapPosts'),
          where('userId', '==', userData.uid)
        );
        const existingSnap = await getDocs(q);
        if (existingSnap.size >= 3) {
          Alert.alert(
            'Listing limit reached',
            'You can have up to 3 gear swap listings at a time. Please delete one before creating a new listing.'
          );
          setPosting(false);
          return;
        }
        const payload = {
          userId: userData.uid,
          username: userData.username || null,
          photoURL: userData.photoURL || null,
          firstName: userData.firstName || null,
          lastName: userData.lastName || null,
          displayName,
          location: location || '',
          listingType,
          items: sanitizedItems,
          cardBackground: cardBackground || null,
          headerTextColor: headerTextColor || null,
          createdAt: serverTimestamp(),
        };
        if (listingType === 'SALE') payload.price = price.trim();
        const listingRef = await addDoc(collection(db, 'gearSwapPosts'), payload);
        await syncCalendarEventsForListing({
          db,
          listingId: listingRef.id,
          listingTitle: displayName || '',
          listingLocation: location || '',
          listingItems: sanitizedItems,
        });
      }
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('GearSwapList', { searchQuery: '' });
      }
    } catch (error) {
      console.error('Error posting:', error);
      Alert.alert('Post failed', 'Failed to publish listing. Please try again.');
    } finally {
      setPosting(false);
    }
  }, [userData, displayName, location, listingType, price, items, cardBackground, headerTextColor, navigation, isEditing, editListingId, syncCalendarEventsForListing]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('GearSwapList')}
          style={styles.backBtn}
          disabled={posting}
        >
          <Ionicons name="arrow-back" size={24} color={ACCENT} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{isEditing ? 'Edit listing' : 'New listing'}</Text>
      </View>

      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
        <View style={[
          styles.card,
          { borderColor: LISTING_TYPES.find(t => t.id === listingType)?.color },
        ]}>
          {/* Clipped background layer */}
          <View style={styles.cardBgLayer}>
            {cardBackground?.type === 'color' && cardBackground.value ? (
              <View style={{ flex: 1, backgroundColor: cardBackground.value }} />
            ) : null}
            {cardBackground?.type === 'image' && cardBackground.value ? (
              <Image source={{ uri: cardBackground.value }} style={{ flex: 1 }} resizeMode="cover" />
            ) : null}
            {cardBackground?.type === 'video' && cardBackground.value && Platform.OS === 'web' ? (
              <video src={cardBackground.value} autoPlay loop muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : null}
          </View>
          <View style={[styles.previewRow, { zIndex: 1 }]}>
            <View style={styles.previewLeft}>
              <Text style={[styles.previewName, headerTextColor ? { color: headerTextColor } : undefined]} numberOfLines={1}>{displayName}</Text>
              <TextInput
                style={[styles.previewLocationInput, headerTextColor ? { color: headerTextColor } : undefined]}
                placeholder="Location"
                value={location}
                onChangeText={setLocation}
                placeholderTextColor="#99999980"
              />
              {/* Header text color swatches */}
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 4 }}>
                <View style={{ flexDirection: 'row', gap: 5, alignItems: 'center' }}>
                  <Text style={{ fontSize: 10, color: '#999', marginRight: 2 }}>Aa</Text>
                  {[null, '#000', '#fff', '#333', '#f5f0e8', '#424251', '#fbcd9d', '#e74c3c', '#27ae60', '#8e44ad'].map((c, i) => (
                    <TouchableOpacity
                      key={i}
                      onPress={() => setHeaderTextColor(c)}
                      style={{
                        width: 16, height: 16, borderRadius: 8,
                        backgroundColor: c || 'transparent',
                        borderWidth: headerTextColor === c ? 2 : 1,
                        borderColor: headerTextColor === c ? ACCENT : '#ccc',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {!c && <Text style={{ fontSize: 9, color: '#555' }}>✕</Text>}
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
            <View style={styles.previewRight}>
              <View style={styles.listingTypeRow}>
                {LISTING_TYPES.map(t => (
                  <TouchableOpacity
                    key={t.id}
                    onPress={() => setListingType(t.id)}
                    style={[styles.listingTypeChip, { borderColor: t.color }, listingType === t.id && { backgroundColor: t.color }]}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.listingTypeChipText, { color: listingType === t.id ? '#fff' : t.color }]}>{t.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {listingType === 'SALE' && (
                <TextInput
                  style={styles.priceInput}
                  placeholder="Price"
                  value={price}
                  onChangeText={setPrice}
                  placeholderTextColor="#999"
                  keyboardType="decimal-pad"
                />
              )}
            </View>
          </View>

          {/* Card toolbar: Templates + Card Background */}
          <View style={styles.toolbar}>
            <View style={styles.toolbarLeft}>
              <TouchableOpacity
                style={[styles.toolbarBtn, showTemplates && styles.toolbarBtnActive]}
                onPress={() => { setShowTemplates(p => !p); setCardBgPanelOpen(false); }}
              >
                <Ionicons name="grid-outline" size={16} color={showTemplates ? '#fff' : '#555'} />
                <Text style={[styles.toolbarBtnText, showTemplates && { color: '#fff' }]}>Templates</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.toolbarBtn, cardBgPanelOpen && styles.toolbarBtnActive]}
                onPress={() => { setCardBgPanelOpen(p => !p); setShowTemplates(false); }}
              >
                <Ionicons name="color-palette-outline" size={16} color={cardBgPanelOpen ? '#fff' : '#555'} />
                <Text style={[styles.toolbarBtnText, cardBgPanelOpen && { color: '#fff' }]}>Card BG</Text>
              </TouchableOpacity>
              {cardBackground && (
                <TouchableOpacity style={styles.toolbarBtn} onPress={() => setCardBackground(null)}>
                  <Ionicons name="close-circle" size={16} color="#555" />
                  <Text style={styles.toolbarBtnText}>Clear BG</Text>
                </TouchableOpacity>
              )}
              {(showTemplates || cardBgPanelOpen) && (
                <TouchableOpacity
                  style={styles.toolbarBtn}
                  onPress={() => { setShowTemplates(false); setCardBgPanelOpen(false); }}
                >
                  <Ionicons name="chevron-up-circle" size={20} color="#555" />
                </TouchableOpacity>
              )}
            </View>
          </View>
          <View style={styles.modeRow}>
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.modeAuxBtn, !canUndo && styles.modeAuxBtnDisabled]}
              onPress={handleUndo}
              disabled={!canUndo}
            >
              <Ionicons name="arrow-undo-outline" size={16} color={!canUndo ? '#9aa' : '#555'} />
              <Text style={[styles.toolbarBtnText, !canUndo && styles.modeAuxBtnTextDisabled]}>Undo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, styles.modeAuxBtn, !canRedo && styles.modeAuxBtnDisabled]}
              onPress={handleRedo}
              disabled={!canRedo}
            >
              <Ionicons name="arrow-redo-outline" size={16} color={!canRedo ? '#9aa' : '#555'} />
              <Text style={[styles.toolbarBtnText, !canRedo && styles.modeAuxBtnTextDisabled]}>Redo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.toolbarBtn, gridMode === 'resize' && styles.toolbarBtnActive]}
              onPress={() => setGridMode((m) => (m === 'move' ? 'resize' : 'move'))}
            >
              <Ionicons name={gridMode === 'move' ? 'move-outline' : 'resize-outline'} size={16} color={gridMode === 'resize' ? '#fff' : '#555'} />
              <Text style={[styles.toolbarBtnText, gridMode === 'resize' && { color: '#fff' }]}>
                {gridMode === 'move' ? 'Move Mode' : 'Resize Mode'}
              </Text>
            </TouchableOpacity>
          </View>
          <MoveModeDesktopNote />

          {/* Grid area: panels float above grid (absolute) */}
          <View style={styles.gridWithPanelsWrap}>
            {(showTemplates || cardBgPanelOpen) && (
              <View style={styles.panelsOverlay} pointerEvents="box-none">
                {showTemplates && (
                  <View style={styles.panel}>
                    <Text style={styles.panelTitle}>Choose a layout template</Text>
                    <View style={styles.templateRow}>
                      {TEMPLATES.map(t => (
                        <TouchableOpacity
                          key={t.id}
                          style={styles.templateBtn}
                          onPress={() => { setItemsWithHistory(prev => mergeTemplateWithCurrent(prev, t.build())); setShowTemplates(false); }}
                        >
                          <Ionicons name={t.icon} size={22} color={ACCENT} />
                          <Text style={styles.templateBtnLabel}>{t.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
                {cardBgPanelOpen && (
                  <View style={styles.panel}>
                    <View style={styles.panelTabRow}>
                      {['color', 'image', 'video'].map(tab => (
                        <TouchableOpacity
                          key={tab}
                          style={[styles.panelTab, cardBgTab === tab && styles.panelTabActive]}
                          onPress={() => setCardBgTab(tab)}
                        >
                          <Text style={[styles.panelTabText, cardBgTab === tab && { color: '#fff' }]}>
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {cardBgTab === 'color' && (
                      <View>
                        <View style={styles.swatchGrid}>
                          {CARD_BG_SWATCHES.map(color => (
                            <TouchableOpacity
                              key={color}
                              style={[
                                styles.bgSwatch,
                                { backgroundColor: color },
                                cardBackground?.type === 'color' && cardBackground.value === color && styles.bgSwatchActive,
                                color === '#ffffff' && { borderWidth: 1, borderColor: '#ddd' },
                              ]}
                              onPress={() => setCardBackground({ type: 'color', value: color })}
                            />
                          ))}
                        </View>
                        <View style={styles.hexRow}>
                          <Text style={styles.hexLabel}>#</Text>
                          <TextInput
                            style={styles.hexInput}
                            placeholder="Custom hex"
                            value={cardBgColorInput}
                            onChangeText={setCardBgColorInput}
                            placeholderTextColor="#999"
                            autoCapitalize="none"
                          />
                          <TouchableOpacity
                            style={styles.hexApplyBtn}
                            onPress={() => {
                              const hex = cardBgColorInput.startsWith('#') ? cardBgColorInput : `#${cardBgColorInput}`;
                              if (/^#[0-9a-fA-F]{6}$/.test(hex)) { setCardBackground({ type: 'color', value: hex }); setCardBgColorInput(''); }
                            }}
                          >
                            <Text style={styles.hexApplyText}>Apply</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                    {(cardBgTab === 'image' || cardBgTab === 'video') && (
                      <View>
                        {cardBackground?.type === cardBgTab && cardBackground.value ? (
                          <View style={styles.bgPreviewWrap}>
                            {cardBgTab === 'image'
                              ? <Image source={{ uri: cardBackground.value }} style={styles.bgPreview} resizeMode="cover" />
                              : <View style={[styles.bgPreview, { backgroundColor: '#222', alignItems: 'center', justifyContent: 'center' }]}><Ionicons name="videocam" size={28} color="#fff" /></View>
                            }
                            <TouchableOpacity onPress={() => setCardBackground(null)} style={styles.bgPreviewRemove}>
                              <Ionicons name="close-circle" size={20} color="rgba(0,0,0,0.6)" />
                            </TouchableOpacity>
                          </View>
                        ) : (
                          <TouchableOpacity style={styles.bgUploadBtn} onPress={() => handleCardBgUpload(cardBgTab)} disabled={uploadingCardBg}>
                            {uploadingCardBg
                              ? <ActivityIndicator size="small" color={ACCENT} />
                              : <><Ionicons name={cardBgTab === 'video' ? 'videocam-outline' : 'image-outline'} size={20} color={ACCENT} /><Text style={styles.bgUploadBtnText}>Upload {cardBgTab}</Text></>
                            }
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                )}
              </View>
            )}

            <View style={[styles.gridWrap, { zIndex: 1 }]}>
              <EditablePostGrid
                items={items}
                setItems={setItemsWithHistory}
                widgetTypes={GEAR_SWAP_WIDGET_TYPES}
                onFileUpload={handleFileUpload}
                accentColor={ACCENT}
                cellSize={CELL_SIZE}
                uploadingCellId={uploadingCellId}
                gridBgColor={cardBackground ? 'transparent' : '#fff'}
                interactionMode={gridMode}
              />
            </View>
          </View>
        </View>

        {/* Primary post button below the card */}
        <View style={{ alignItems: 'center', marginTop: 24, marginBottom: 32 }}>
          <TouchableOpacity
            style={[styles.postBtn, posting && styles.postBtnDisabled]}
            onPress={handlePost}
            disabled={posting}
            activeOpacity={0.8}
          >
            {posting ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <Text style={styles.postBtnText}>POST</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    paddingTop: Platform.OS === 'web' ? 80 : 70,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backBtn: { padding: 8, marginRight: 8 },
  headerTitle: {
    fontFamily: 'Glacial Indifference',
    fontSize: 20,
    fontWeight: '600',
    color: ACCENT,
    flex: 1,
  },
  scroll: { flex: 1 },
  scrollContent: { paddingHorizontal: 20, paddingBottom: 40 },
  postBtn: {
    paddingVertical: 10,
    paddingHorizontal: 32,
    borderRadius: 999,
    backgroundColor: '#000000',
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  postBtnDisabled: { opacity: 0.7 },
  postBtnText: {
    fontFamily: 'Playpen Sans',
    fontSize: 20,
    marginBottom: 0,
    color: '#ffffff',
  },
  toolbar: { flexDirection: 'row', justifyContent: 'space-between', gap: 8, marginBottom: 8, alignItems: 'center' },
  toolbarLeft: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  modeRow: { width: '100%', flexDirection: 'row', marginBottom: 8 },
  modeAuxBtn: { backgroundColor: '#f7f7f7' },
  modeAuxBtnDisabled: { opacity: 0.5 },
  modeAuxBtnTextDisabled: { color: '#9aa' },
  toolbarBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8,
    backgroundColor: '#f0f0f0', borderWidth: 1, borderColor: '#ddd',
  },
  toolbarBtnActive: { backgroundColor: ACCENT, borderColor: ACCENT },
  toolbarBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 11, color: '#555' },
  gridWithPanelsWrap: {
    position: 'relative',
    zIndex: 1,
  },
  panelsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    paddingBottom: 8,
  },
  panel: {
    backgroundColor: '#fff', borderRadius: 10, padding: 12,
    marginBottom: 10, maxWidth: 400, alignSelf: 'center', width: '100%',
    borderWidth: 1, borderColor: '#eee',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6, elevation: 2,
  },
  panelTitle: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#555', marginBottom: 10 },
  panelTabRow: { flexDirection: 'row', gap: 6, marginBottom: 10 },
  panelTab: { paddingVertical: 5, paddingHorizontal: 12, borderRadius: 6, backgroundColor: '#f0f0f0' },
  panelTabActive: { backgroundColor: ACCENT },
  panelTabText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#333' },
  templateRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  templateBtn: { alignItems: 'center', gap: 4, padding: 10, borderRadius: 8, backgroundColor: '#f5f5f5', borderWidth: 1, borderColor: '#e0e0e0', minWidth: 60 },
  templateBtnLabel: { fontFamily: 'VastagoGrotesk', fontSize: 10, color: '#333' },
  swatchGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  bgSwatch: { width: 32, height: 32, borderRadius: 16 },
  bgSwatchActive: { borderWidth: 3, borderColor: ACCENT },
  hexRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  hexLabel: { fontFamily: 'VastagoGrotesk', fontSize: 14, color: '#555' },
  hexInput: { flex: 1, fontFamily: 'VastagoGrotesk', fontSize: 13, color: '#333', borderWidth: 1, borderColor: '#ddd', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 5 },
  hexApplyBtn: { paddingVertical: 5, paddingHorizontal: 12, backgroundColor: ACCENT, borderRadius: 6 },
  hexApplyText: { fontFamily: 'VastagoGrotesk', fontSize: 12, color: '#fff' },
  bgUploadBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 8, borderWidth: 1.5, borderColor: ACCENT, borderStyle: 'dashed' },
  bgUploadBtnText: { fontFamily: 'VastagoGrotesk', fontSize: 13, color: ACCENT },
  bgPreviewWrap: { position: 'relative', alignSelf: 'flex-start' },
  bgPreview: { width: 120, height: 80, borderRadius: 6 },
  bgPreviewRemove: { position: 'absolute', top: -8, right: -8 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    zIndex: 20,
    marginBottom: 24,
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
    borderWidth: 2,
    overflow: 'visible',
    position: 'relative',
  },
  cardBgLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    overflow: 'hidden',
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  previewLeft: { flex: 1 },
  previewName: {
    fontFamily: 'Computer Says No',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 24,
      laptop: 28,
      desktop: 32
    }),
    color: '#000',
    marginBottom: 4,
  },
  previewLocationInput: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#333',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  previewRight: { alignItems: 'flex-end' },
  listingTypeRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  listingTypeChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
  },
  listingTypeChipText: { fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' },
  priceInput: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#333',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    maxWidth: 70,
  },
  gridWrap: { marginTop: 8 },
});
